import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

import {
  StyledFormattingToolbar,
  StyledToolbarButton,
  stopClick,
} from "./elements";
import { CardResizeHandlers } from "./linkElement";
import {
  ArrowRefreshIcon,
  AttachmentIcon,
  CancelCrossIcon,
  DownloadIcon,
  TrashIcon,
  UploadIcon,
} from "../../components/Icons";
import { SpinnerLoader } from "../../components/Loaders";
import { ProgressRing } from "../../components/ProgressRing";
import {
  StackedCard,
  StackedCardCover,
  StackedCardRow,
} from "../../components/StackedCard";
import { Tooltip } from "../../components/Tooltip";
import { Body2, Caption1, Caption2 } from "../../components/Typography";
import { COVER_RATIO_IMAGE, Placeholder } from "../stackedCard/content";

/* ------------------------------------------------------------------ */
/* File element — the upload lifecycle + the file card                 */
/* ------------------------------------------------------------------ */

/**
 * The file card is the second element built on `StackedCard`, and it mirrors the
 * card in a project's Files tab (`Folders/GridView/Items/File.js`): a thumbnail
 * over a strip with the name, then size · date.
 *
 * Its lifecycle is the app's real upload lifecycle, which is two phases and not
 * one — the distinction the ring alone doesn't tell you:
 *
 *   uploading   XHR progress 0–100. `ProgressRing radius={22} stroke={4}` with
 *               the percentage, exactly as `Items/FileUpload.js` does it.
 *   preparing   bytes are in, the server is generating the thumbnail. Progress
 *               is no longer knowable, so the ring gives way to the
 *               indeterminate `SpinnerLoader` at the same size — again as the
 *               real card does at `progress === 100`.
 *   ready       the thumbnail exists; the card renders it.
 *   error       the upload failed: retry, same as the app's `ArrowRefreshIcon`.
 *
 * Both phases reserve the same box as the finished cover, so a card never
 * reflows as it climbs — the discipline the unfurl ladder established.
 */

/* The Files-tab card is a fixed 208×208. On a canvas the width is the host's,
   so 208 is only the default a click-placed card takes. */
export const FILE_DEFAULT_WIDTH = 208;

/* Big enough to be worth refusing, so the error state is reachable: the server
   enforces the real limits (and answers StorageOverusedError when full). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const UPLOAD_TICK_MS = 110;
export const UPLOAD_STEP = 9;
/* How long the server spends making a thumbnail once the bytes have landed. */
export const PREPARING_MS = 1100;

/** What the mock keeps about a picked file. */
export interface PickedFile {
  /** Name without the extension — truncates on its own. */
  base: string;
  /** Extension without the dot; never truncated, as in the real card. */
  extension: string;
  size: number;
  mimeType: string;
  /** An object URL, so an image really renders. Null for everything else. */
  thumbnailUrl: string | null;
}

/** A file element on the board. Position lives in the shared positions map. */
export interface FileElementDef {
  id: string;
  width: number;
  /** null while the card is an empty placeholder waiting for a file. */
  file: PickedFile | null;
}

/* Angie.functions.format_file_size — two decimals, no space, lower-case k.
   "1.20MB", not "1.2 MB". Reproduced exactly so the mock reads like the app. */
export const FILE_SIZE_UNITS: [string, number][] = [
  ["TB", 1099511627776],
  ["GB", 1073741824],
  ["MB", 1048576],
  ["kB", 1024],
];

export const formatFileSize = (value: number): string => {
  for (const [unit, bytes] of FILE_SIZE_UNITS) {
    const inUnit = value / bytes;
    if (inUnit > 0.9) {
      return `${inUnit.toFixed(2)}${unit}`;
    }
  }

  return `${Math.trunc(value)}B`;
};

export const describeFile = (file: File): PickedFile => {
  const dot = file.name.lastIndexOf(".");
  const hasExtension = dot > 0;

  return {
    base: hasExtension ? file.name.slice(0, dot) : file.name,
    extension: hasExtension ? file.name.slice(dot + 1) : "",
    size: file.size,
    mimeType: file.type,
    // Images get a real thumbnail; the server would generate one for every type,
    // but the mock can only show what the browser can already decode.
    thumbnailUrl: file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null,
  };
};

/* Object URLs are a manual allocation — release one whenever the file it stands
   for stops being on the board. */
export const revokeThumbnail = (file: PickedFile | null): void => {
  if (file?.thumbnailUrl) {
    URL.revokeObjectURL(file.thumbnailUrl);
  }
};

/** The first actual file on a clipboard or a drop, or null if there is none. */
export const binaryFrom = (data: DataTransfer | null): File | null => {
  if (!data) {
    return null;
  }

  if (data.files && data.files.length > 0) {
    return data.files[0];
  }

  // A screenshot paste arrives as an item, not in `files`.
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === "file") {
      const picked = item.getAsFile();
      if (picked) {
        return picked;
      }
    }
  }

  return null;
};

export type UploadStatus = "uploading" | "preparing" | "ready" | "error";

export interface UploadController {
  status: UploadStatus;
  /** 0–100, as the app's XHR reports it. Only meaningful while uploading. */
  progress: number;
  retry: () => void;
}

/**
 * Drives one file through the upload lifecycle. Stands in for
 * `useFileHandler` + the XHR in `utils/upload.jsx`: ticks progress to 100,
 * hands over to the thumbnail phase, then resolves.
 */
export const useMockUpload = (file: PickedFile | null): UploadController => {
  const [status, setStatus] = useState<UploadStatus>("uploading");
  const [progress, setProgress] = useState(0);

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef(0);

  const clearAll = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (ticker.current) {
      clearInterval(ticker.current);
      ticker.current = null;
    }
  }, []);

  const retry = useCallback(() => {
    clearAll();
    progressRef.current = 0;
    setProgress(0);
    setStatus("uploading");

    if (!file) {
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      timers.current.push(setTimeout(() => setStatus("error"), 700));

      return;
    }

    ticker.current = setInterval(() => {
      progressRef.current = Math.min(100, progressRef.current + UPLOAD_STEP);
      setProgress(progressRef.current);

      if (progressRef.current >= 100) {
        clearAll();
        // The bytes are up; the thumbnail is the server's job now.
        setStatus("preparing");
        timers.current.push(setTimeout(() => setStatus("ready"), PREPARING_MS));
      }
    }, UPLOAD_TICK_MS);
  }, [clearAll, file]);

  useEffect(() => {
    retry();

    return clearAll;
  }, [retry, clearAll]);

  return { status, progress, retry };
};

/* The card fills the box the board drew; height stays auto. */
export const StyledFileCard = styled(StackedCard)`
  && {
    width: 100%;
  }

  user-select: none;
`;

/* The cover while uploading or preparing — the ring/spinner centred in the box
   the finished thumbnail will occupy, so resolving never reflows the card. */
export const StyledUploadCover = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 100%;
  background-color: var(--color-theme-200);
`;

/* No thumbnail (the browser can't decode a .zip): the clip plus the extension.
   The real card has NO type-icon fallback — it renders a bare <img> and trusts
   the server — so this is net-new and worth a decision. */
export const StyledFileFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  background-color: var(--color-theme-200);
  color: var(--color-theme-600);

  > svg {
    width: 36px;
    height: 36px;
    fill: var(--color-theme-500);
  }
`;

/* Name row: the base name truncates, the extension never does — the same split
   the real card makes with removeExtensionFromFileName. */
export const StyledFileName = styled.div`
  display: flex;
  align-items: baseline;
  min-width: 0;
  width: 100%;

  .file-base {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-ext {
    flex-shrink: 0;
  }
`;

/* The drop overlay, from directive.ang_drop_files.less: a near-opaque cover
   with a 2px dashed primary border and "Drop here". The app sets 24px type for
   a full-page target; on a card it reads at Body2. */
export const StyledDropOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
  background-color: var(--page-paper-main);

  .drop-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border: 2px dashed var(--color-primary);
    border-radius: 6px;
  }
`;

export const StyledHiddenFileInput = styled.input`
  display: none;
`;

/* The placeholder reserves the same box the finished cover will occupy — the
   cover's ratio, not a pixel height — so attaching a file does not resize the
   card under the pointer. Placeholder's own fixed height is overridden here;
   the descendant selector outranks its class. */
export const StyledPlaceholderBox = styled.div`
  width: 100%;
  aspect-ratio: ${COVER_RATIO_IMAGE};

  > button {
    height: 100%;
  }
`;

/* The interactive empty state, in the shared placeholder frame the other card
   types use. Doubles as the drop target, like its copy promises. */
export const FilePlaceholder = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
): ReactElement => (
  <StyledPlaceholderBox>
    {/* Fills the card, so it has to be draggable as well as clickable. */}
    <Placeholder
      icon={<AttachmentIcon />}
      label="Drop a file or click to upload"
      dashed
      data-drag-through=""
      {...props}
    />
  </StyledPlaceholderBox>
);

export interface FileToolbarProps {
  status: UploadStatus | "placeholder";
  /* Each phase only supplies the actions its own buttons need. */
  onPick?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onDelete: () => void;
}

/* The file element's floating toolbar. The leading action is whatever the
   current phase makes meaningful; Move to Trash is always available. */
export const FileFormattingToolbar = ({
  status,
  onPick,
  onCancel,
  onRetry,
  onDelete,
}: FileToolbarProps): ReactElement => (
  <StyledFormattingToolbar onMouseDown={stopClick} onClick={stopClick}>
    {status === "placeholder" ? (
      <StyledToolbarButton type="button" onClick={onPick}>
        <UploadIcon className="i16" />
        Upload
      </StyledToolbarButton>
    ) : null}
    {status === "uploading" || status === "preparing" ? (
      <StyledToolbarButton type="button" onClick={onCancel}>
        <CancelCrossIcon className="i16" />
        Cancel
      </StyledToolbarButton>
    ) : null}
    {status === "error" ? (
      <>
        <StyledToolbarButton type="button" onClick={onRetry}>
          <ArrowRefreshIcon className="i16" />
          Retry
        </StyledToolbarButton>
        {/* A failed upload needs a way back to the placeholder too — otherwise
            a file that can never succeed leaves trashing the card as the only
            exit. */}
        <StyledToolbarButton type="button" onClick={onCancel}>
          <CancelCrossIcon className="i16" />
          Cancel
        </StyledToolbarButton>
      </>
    ) : null}
    {/* Inert, like the board's other export-shaped actions — there is no real
        stored file behind a mock. */}
    {status === "ready" ? (
      <StyledToolbarButton type="button">
        <DownloadIcon className="i16" />
        Download
      </StyledToolbarButton>
    ) : null}
    <span className="divider" />
    <Tooltip title="Move to Trash">
      <StyledToolbarButton
        type="button"
        className="icon-only"
        onClick={onDelete}
      >
        <TrashIcon className="i16" />
      </StyledToolbarButton>
    </Tooltip>
  </StyledFormattingToolbar>
);

export interface FileUploadCardProps {
  element: FileElementDef;
  selected: boolean;
  resize: CardResizeHandlers;
  onCancel: () => void;
  onDelete: () => void;
}

/**
 * A file element with a file attached: the card runs the upload lifecycle and
 * then renders the finished preview.
 */
export const FileUploadCard = ({
  element,
  selected,
  resize,
  onCancel,
  onDelete,
}: FileUploadCardProps): ReactElement => {
  const file = element.file;
  const upload = useMockUpload(file);
  const ready = upload.status === "ready";

  return (
    <>
      <StyledFileCard
        selected={selected}
        resizable={selected}
        resizeAxis="width"
        resizeLabel="Resize file card"
        {...resize}
      >
        <StackedCardCover aspectRatio={COVER_RATIO_IMAGE}>
          {ready ? (
            renderFileThumbnail(file)
          ) : (
            <StyledUploadCover>
              {/* Determinate while the bytes move, indeterminate once they have
                  landed — the app's own switch at progress === 100. */}
              {upload.status === "uploading" ? (
                <ProgressRing progress={upload.progress} radius={22} stroke={4}>
                  <Caption2 weight="bold" color="secondary">
                    {upload.progress}%
                  </Caption2>
                </ProgressRing>
              ) : null}
              {upload.status === "preparing" ? (
                <SpinnerLoader radius={22} stroke={4} />
              ) : null}
              {upload.status === "error" ? (
                <ArrowRefreshIcon
                  style={{ cursor: "pointer" }}
                  onClick={upload.retry}
                />
              ) : null}
              <Caption2 color="tertiary">
                {upload.status === "uploading" ? "Uploading…" : null}
                {upload.status === "preparing" ? "Preparing preview…" : null}
                {upload.status === "error" ? "Upload failed" : null}
              </Caption2>
            </StyledUploadCover>
          )}
        </StackedCardCover>

        <StackedCardRow role="title">
          <StyledFileName>
            <Caption1 weight="bold" className="file-base">
              {file?.base}
            </Caption1>
            {file?.extension ? (
              <Caption1 weight="bold" className="file-ext">
                .{file.extension}
              </Caption1>
            ) : null}
          </StyledFileName>
        </StackedCardRow>

        {/* The real card hides its metadata until hover (max-height 0 → 55px).
            On a canvas the card is the object, not a row in a dense grid, so the
            line stays up — but it only says anything once the file exists. */}
        <StackedCardRow role="meta">
          <Caption2 color="tertiary">
            {ready && file ? `${formatFileSize(file.size)} · Just now` : " "}
          </Caption2>
        </StackedCardRow>
      </StyledFileCard>
      {selected ? (
        <FileFormattingToolbar
          status={upload.status}
          onCancel={onCancel}
          onRetry={upload.retry}
          onDelete={onDelete}
        />
      ) : null}
    </>
  );
};

/* An image renders for real; anything else falls back to the clip + extension. */
export const renderFileThumbnail = (file: PickedFile | null): ReactElement =>
  file?.thumbnailUrl ? (
    <img src={file.thumbnailUrl} alt="" />
  ) : (
    <StyledFileFallback>
      <AttachmentIcon />
      <Caption2 color="tertiary">
        {file?.extension ? file.extension.toUpperCase() : "FILE"}
      </Caption2>
    </StyledFileFallback>
  );

export interface FileElementProps {
  element: FileElementDef;
  selected: boolean;
  /** Open the file picker as soon as a freshly drawn placeholder lands. */
  autoOpen: boolean;
  onPickFile: (file: File) => void;
  resize: CardResizeHandlers;
  onCancel: () => void;
  onDelete: () => void;
}

/**
 * The two shapes of a file element: an empty placeholder that can be clicked or
 * dropped onto, and the uploading/ready card.
 *
 * A box drawn with the File tool opens the picker immediately. Dismissing the
 * picker fires no change, so the card simply stays a placeholder — which is what
 * makes Esc a no-op by construction rather than by handling.
 */
export const FileElement = ({
  element,
  selected,
  autoOpen,
  onPickFile,
  resize,
  onCancel,
  onDelete,
}: FileElementProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dropping, setDropping] = useState(false);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  useEffect(() => {
    if (autoOpen) {
      openPicker();
    }
  }, [autoOpen, openPicker]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const picked = event.target.files?.[0];
      if (picked) {
        onPickFile(picked);
      }
      // Let the same file be chosen again after a cancel or a delete.
      event.target.value = "";
    },
    [onPickFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      setDropping(false);

      const dropped = binaryFrom(event.dataTransfer);
      if (dropped) {
        onPickFile(dropped);
      }
    },
    [onPickFile]
  );

  const input = (
    <StyledHiddenFileInput
      ref={inputRef}
      type="file"
      onChange={handleInputChange}
    />
  );

  if (element.file != null) {
    return (
      <>
        {input}
        <FileUploadCard
          element={element}
          selected={selected}
          resize={resize}
          onCancel={onCancel}
          onDelete={onDelete}
        />
      </>
    );
  }

  return (
    <>
      {input}
      <StyledFileCard selected={selected} aria-label="Empty file card">
        <StackedCardRow
          role="custom"
          bleed
          /* Marks this row as owning its own drops, so the board-level handler
             stands aside and lets the card take the file. */
          data-file-dropzone=""
          onDragEnter={(event) => {
            event.preventDefault();
            setDropping(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            // Ignore the leaves fired when crossing onto a child.
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setDropping(false);
            }
          }}
          onDrop={handleDrop}
        >
          <FilePlaceholder onClick={openPicker} />
          {dropping ? (
            <StyledDropOverlay>
              <span className="drop-box">
                <Body2 color="secondary">Drop here</Body2>
              </span>
            </StyledDropOverlay>
          ) : null}
        </StackedCardRow>
      </StyledFileCard>
      {selected ? (
        <FileFormattingToolbar
          status="placeholder"
          onPick={openPicker}
          onDelete={onDelete}
        />
      ) : null}
    </>
  );
};
